/**
 * Exercise Preview Component
 * Modal for previewing exercise content as participants will see it
 */

import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import { MarkdownRenderer } from "@components/exercise";
import { PresentationViewer } from "@components/content";
import type { Exercise } from "@/types";

// Icons
const CloseIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const PresentationIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const CodeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    />
  </svg>
);

const InteractiveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
    />
  </svg>
);

export interface ExercisePreviewProps {
  exercise: Exercise;
  isOpen: boolean;
  onClose: () => void;
}

function getContentTypeIcon(contentType: string) {
  switch (contentType) {
    case "presentation":
      return <PresentationIcon className="w-5 h-5" />;
    case "jupyter":
      return <CodeIcon className="w-5 h-5" />;
    case "interactive":
      return <InteractiveIcon className="w-5 h-5" />;
    default:
      return <DocumentIcon className="w-5 h-5" />;
  }
}

function getContentTypeLabel(contentType: string) {
  switch (contentType) {
    case "presentation":
      return "Presentation";
    case "jupyter":
      return "Jupyter Notebook";
    case "interactive":
      return "Interactive Exercise";
    case "markdown":
      return "Markdown";
    default:
      return contentType;
  }
}

export function ExercisePreview({
  exercise,
  isOpen,
  onClose,
}: ExercisePreviewProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Render content based on content_type
  const renderContent = () => {
    switch (exercise.content_type) {
      case "presentation":
        return (
          <PresentationViewer exerciseId={exercise.id} title={exercise.title} />
        );

      case "markdown":
        if (exercise.content_markdown) {
          return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
              <MarkdownRenderer content={exercise.content_markdown} />
            </div>
          );
        }
        return (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <DocumentIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No markdown content available for this exercise.</p>
            {exercise.content_path && (
              <p className="mt-2 text-sm font-mono">{exercise.content_path}</p>
            )}
          </div>
        );

      case "jupyter":
        return (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <CodeIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium mb-2">Jupyter Notebook</p>
            <p className="text-sm">
              Jupyter notebooks are displayed in a specialized viewer when
              accessed by participants.
            </p>
            {exercise.content_path && (
              <p className="mt-4 text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg inline-block">
                {exercise.content_path}
              </p>
            )}
          </div>
        );

      case "interactive":
        return (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <InteractiveIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium mb-2">Interactive Exercise</p>
            <p className="text-sm">
              Interactive exercises provide hands-on learning experiences for
              participants.
            </p>
            {exercise.content_path && (
              <p className="mt-4 text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg inline-block">
                {exercise.content_path}
              </p>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <DocumentIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Preview not available for this content type.</p>
          </div>
        );
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => {
        if (e.target === overlayRef.current) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-title"
    >
      <div
        ref={contentRef}
        className="w-full max-w-5xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
              {getContentTypeIcon(exercise.content_type)}
            </div>
            <div>
              <h2
                id="preview-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                {exercise.title}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium">
                  {getContentTypeLabel(exercise.content_type)}
                </span>
                <span>Order: #{exercise.order_index}</span>
                {exercise.estimated_minutes && (
                  <span>{exercise.estimated_minutes} min</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-gray-500 mr-2">
              Preview Mode
            </span>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close preview"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Description (if available) */}
        {exercise.description && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {exercise.description}
            </p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">{renderContent()}</div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This preview shows how participants will see this exercise
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
